import React from "react";
import { Skeleton } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronRight } from "lucide-react";
import UniversalDropdown from "@/components/UniversalDropdown";
import { AUTH_ROLES } from "@/constants/auth";
import useStore from "@/hooks/useStore";
import { getPayload as getImpersonationPayload, isActive as isImpersonationActive } from "@/impersonation/impersonationSession";
import { getUserProfileNameParts } from "@/utils/getUserProfileNameParts";
import { capitalize } from "@/utils/stringUtils";

const ActingUserStrip = observer(
  ({ isCollapsed, isMobile, isActingUserPickerOpen, onToggleActingUserPicker, onStopActing }) => {
    const { authStore } = useStore();
    const rowCollapsed = isCollapsed && !isMobile;
    const chevronClassName = `chevron-icon${isActingUserPickerOpen ? " open" : ""}`;

    const payload = getImpersonationPayload();
    const acting = isImpersonationActive();
    const displayName =
      String(payload?.realEmail ?? "").trim() ||
      String(payload?.realUserId ?? "").trim() ||
      String(authStore?.getSignedInEmail?.() ?? "").trim() ||
      String(authStore?.cognitoUserId ?? "").trim() ||
      "User";
    const prof = acting ? getUserProfileNameParts(null) : getUserProfileNameParts(authStore?.userProfile);
    const rawRole = authStore?.cognitoIsSuperAdmin ? AUTH_ROLES.SUPERADMIN : String(authStore?.role ?? "").trim();
    const displayRole = rawRole
      ? rawRole === AUTH_ROLES.REALTOR
        ? "Real Estate Agent"
        : capitalize(rawRole, true)
      : "";
    const isDisplayNamePending = false;

    const first = String(prof.first ?? "").trim();
    const last = String(prof.last ?? "").trim();
    let signedInAvatarInitial = "U";
    if (first && last) {
      signedInAvatarInitial = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    } else if (first) {
      signedInAvatarInitial = first.charAt(0).toUpperCase();
    } else if (last) {
      signedInAvatarInitial = last.charAt(0).toUpperCase();
    } else {
      signedInAvatarInitial = (displayName || "U").charAt(0).toUpperCase();
    }

    const effectiveEmail = acting ? String(payload?.targetEmail ?? "").trim() : "";
    const actingFullName = acting
      ? String((payload?.targetDisplayName ?? effectiveEmail) || "User").trim() || "User"
      : "";

    const actingRoles = acting && Array.isArray(authStore?.availableRoles) ? authStore.availableRoles : [];
    const actingRoleSlug =
      acting && actingRoles.length > 0
        ? String(authStore?.activeRole || authStore?.role || actingRoles[0] || "")
            .trim()
            .toLowerCase()
        : "";
    const actingRoleLabel =
      actingRoleSlug && actingRoles.includes(actingRoleSlug)
        ? actingRoleSlug === AUTH_ROLES.REALTOR
          ? "Real Estate Agent"
          : capitalize(actingRoleSlug, true)
        : "";
    const actingMultiRole = acting && actingRoles.length > 1;

    const selectActingRole = (role) => {
      authStore?.setActiveRole?.(role);
    };

    const ariaLabel = acting
      ? isDisplayNamePending
        ? "Signed in user. Acting as user. Open acting user picker."
        : `Signed in as ${displayName}. Acting as ${actingFullName}. Open acting user picker.`
      : isDisplayNamePending
        ? "Signed in user. Open acting user picker."
        : `Signed in as ${displayName}. Open acting user picker.`;

    return (
      <div
        className={`acting-user-strip ${rowCollapsed ? "collapsed" : ""}`}
        data-testid="acting-user-strip"
      >
        <button
          type="button"
          className={`account-switcher-button${isDisplayNamePending ? "" : " account-user-hydrated"}`}
          onClick={onToggleActingUserPicker}
          aria-label={ariaLabel}
          aria-expanded={isActingUserPickerOpen}
          style={{ cursor: "pointer" }}
        >
          <div className="account-avatar" style={isDisplayNamePending ? undefined : { background: "#9D6AC4" }}>
            {isDisplayNamePending ? (
              <Skeleton
                data-testid="account-avatar-skeleton"
                variant="circular"
                animation="wave"
                width={40}
                height={40}
                sx={{ marginBottom: "1px" }}
              />
            ) : (
              <span
                className="account-initial"
                style={{
                  color: "#ffffff",
                  WebkitTextFillColor: "#ffffff",
                  textFillColor: "#ffffff",
                  display: "block",
                }}
              >
                {signedInAvatarInitial}
              </span>
            )}
          </div>
          {!rowCollapsed && (
            <>
              <div className="account-info">
                <div className="account-name" title={isDisplayNamePending ? undefined : displayName}>
                  {isDisplayNamePending ? (
                    <Skeleton variant="text" animation="wave" width="72%" height={20} sx={{ borderRadius: "4px", marginBottom: "1px" }} />
                  ) : (
                    displayName
                  )}
                </div>
                <div className="account-role" title={isDisplayNamePending ? undefined : displayRole || undefined}>
                  {isDisplayNamePending ? (
                    <Skeleton
                      data-testid="account-role-skeleton"
                      variant="text"
                      animation="wave"
                      width="48%"
                      height={18}
                      sx={{ borderRadius: "4px", marginBottom: "1px" }}
                    />
                  ) : (
                    displayRole
                  )}
                </div>
              </div>
              <ChevronRight className={chevronClassName} />
            </>
          )}
        </button>

        {acting ? (
          <div className="acting-user-context-strip">
            <div className="acting-user-context-kicker">now acting as</div>
            <div className="acting-user-context-name" title={effectiveEmail || actingFullName}>
              {actingFullName}
            </div>
            <div className="acting-user-context-email">{effectiveEmail}</div>
            {actingRoleLabel ? (
              actingMultiRole ? (
                <div className="acting-user-context-role">
                  <UniversalDropdown
                    matchTriggerWidth
                    trigger={({ onClick, open }) => (
                      <button
                        type="button"
                        className="view-selector-button"
                        onClick={onClick}
                        aria-haspopup="menu"
                        aria-expanded={open}
                        aria-label={`Current view: ${actingRoleLabel}. Select view.`}
                      >
                        <span className="view-selector-value">{actingRoleLabel}</span>
                        <ChevronDown className="view-selector-chevron" size={14} aria-hidden />
                      </button>
                    )}
                    items={actingRoles.map((role) => {
                      const isSelected = role === actingRoleSlug;
                      return {
                        id: role,
                        selected: isSelected,
                        testId: `acting-view-option-${role}`,
                        onClick: () => selectActingRole(role),
                        label: (
                          <span>
                            {role === AUTH_ROLES.REALTOR ? "Real Estate Agent" : capitalize(role, true)}
                          </span>
                        ),
                        sx: {
                          borderRadius: "6px",
                          py: 1,
                          px: 1.5,
                          minHeight: 32,
                          color: isSelected ? "#9D6AC4" : "#374151",
                          bgcolor: isSelected ? "#F3F0FF" : "transparent",
                          "&:hover": {
                            bgcolor: isSelected ? "#F3F0FF" : "#F3F4F6",
                          },
                          "&.Mui-selected": {
                            color: "#9D6AC4",
                            bgcolor: "#F3F0FF",
                            "&:hover": { bgcolor: "#F3F0FF" },
                          },
                        },
                      };
                    })}
                    menuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                      slotProps: {
                        list: { sx: { py: 0.5 } },
                        paper: {
                          className: "view-selector-menu",
                          sx: {
                            mt: 0.5,
                            borderColor: "#E5E7EB",
                            borderRadius: "8px",
                            boxShadow: "0 8px 20px rgba(17, 24, 39, 0.12)",
                          },
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <div className="acting-user-context-role">{actingRoleLabel}</div>
              )
            ) : null}
            <button
              type="button"
              className="acting-user-context-stop-btn"
              onClick={() => onStopActing?.()}
              aria-label="Stop acting as this user"
            >
              Stop
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);

export default ActingUserStrip;
